// Ce qui a changé après une écriture — grand livre : rapprochement, affectation, avoir.
//
// Le problème n'est pas la lenteur, c'est l'illisibilité. La frame revient entièrement
// rendue : mêmes colonnes, mêmes vingt lignes, et quelque part là-dedans un solde qui n'est
// plus le même et une écriture qui vient de passer en « affectée ». L'utilisateur relit tout
// pour retrouver ce qu'il vient de faire.
//
// Deux réponses, sur la seule région marquée `data-motion-diff` :
//
//   • un montant marqué `data-motion-amount` ÉVOLUE de l'ancien chiffre au nouveau au lieu
//     d'être remplacé. C'est la convention de tous les outils financiers, et pour une bonne
//     raison : le sens et l'ampleur de la variation se lisent sans comparer deux nombres de
//     mémoire ;
//   • une ligne marquée `data-motion-key` dont le contenu a changé s'allume brièvement.
//
// Garde-fou : au-delà de CHANGED_ROWS_CEILING lignes modifiées, on n'allume rien. Ce n'est
// alors plus une écriture mais un changement de vue (filtre de période, de type de mouvement),
// où tout a bougé — tout allumer n'apprendrait rien et donnerait un sapin de Noël.

const REGION_SELECTOR = '[data-motion-diff]';
// Un chiffre qui défile est du mouvement, même sans animation CSS : le garde-fou global de
// `config/_motion.scss` ne peut rien pour lui, il faut le demander ici.
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const TWEEN_MS = 420;
const CHANGED_ROWS_CEILING = 5;

type Snapshot = {
  amounts: Map<string, string>;
  rows: Map<string, string>;
  widths: Map<string, string>;
  present: Set<string>;
};

const snapshots = new WeakMap<Element, Snapshot>();
// Une région remplacée n'est plus la même élément : la clé faible ne suffit pas quand Turbo
// rend la frame sans la recréer. L'id de la frame, lui, traverse le rendu.
const snapshotsById = new Map<string, Snapshot>();

const regionKey = (region: Element) => region.id || null;

// Un instantané ne sert qu'une fois. Le rendu de frame est comparé par deux chemins — le rendu
// emballé, puis `turbo:frame-load` en repli — et c'est le premier arrivé qui l'utilise : sans
// cela, le second rejouerait la réinjection sur une barre déjà en mouvement et la ferait repartir
// de zéro. `expected` protège du cas inverse : un instantané plus récent ne doit pas être
// consommé par un rendu plus ancien.
const consumeSnapshot = (region: Element, expected?: Snapshot): Snapshot | null => {
  const key = regionKey(region);
  const stored = snapshots.get(region) || (key ? snapshotsById.get(key) : undefined);
  if (!stored || (expected && stored !== expected)) return null;

  snapshots.delete(region);
  if (key) snapshotsById.delete(key);
  return stored;
};

const takeSnapshot = (region: Element): Snapshot => ({
  amounts: new Map(
    [...region.querySelectorAll<HTMLElement>('[data-motion-amount]')].map((el) => [
      el.dataset.motionAmount || '',
      el.textContent?.trim() || '',
    ])
  ),
  rows: new Map(
    [...region.querySelectorAll<HTMLElement>('[data-motion-key]')].map((el) => [
      el.dataset.motionKey || '',
      el.textContent?.replace(/\s+/g, ' ').trim() || '',
    ])
  ),
  widths: new Map(
    [...region.querySelectorAll<HTMLElement>('[data-motion-width]')].map((el) => [
      el.dataset.motionWidth || '',
      el.style.getPropertyValue('--workflow-progress').trim(),
    ])
  ),
  present: new Set(
    [...region.querySelectorAll<HTMLElement>('[data-motion-enter]')].map((el) => el.dataset.motionEnter || '')
  ),
});

// Le format du nombre s'apprend sur le texte lui-même plutôt que d'être redéclaré ici : le
// serveur écrit « -1234,56 », un autre écran écrirait « 1 234.56 ». Réimplémenter la mise en
// forme côté client, c'est se condamner à diverger d'elle un jour.
type NumberShape = { decimalSep: string; groupSep: string; decimals: number; prefix: string; suffix: string };

const readNumber = (text: string): { value: number; shape: NumberShape } | null => {
  const match = text.match(/^(\D*?)(-?[\d  ., ]*\d)(\D*)$/);
  if (!match) return null;

  const [, prefix, body, suffix] = match;
  const lastComma = body.lastIndexOf(',');
  const lastDot = body.lastIndexOf('.');
  const decimalSep = lastComma > lastDot ? ',' : lastDot > lastComma ? '.' : '';
  const groupSep = body.match(/[  ]/) ? body.match(/[  ]/)![0] : decimalSep === ',' ? '' : '';

  const digitsOnly = decimalSep
    ? body.slice(0, body.lastIndexOf(decimalSep)).replace(/[^\d-]/g, '') +
      '.' +
      body.slice(body.lastIndexOf(decimalSep) + 1).replace(/\D/g, '')
    : body.replace(/[^\d-]/g, '');

  const value = Number(digitsOnly);
  if (!Number.isFinite(value)) return null;

  const decimals = decimalSep ? body.length - body.lastIndexOf(decimalSep) - 1 : 0;
  return { value, shape: { decimalSep, groupSep, decimals, prefix, suffix } };
};

const formatNumber = (value: number, shape: NumberShape): string => {
  const fixed = Math.abs(value).toFixed(shape.decimals);
  const [whole, fraction] = fixed.split('.');
  const grouped = shape.groupSep ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, shape.groupSep) : whole;
  const sign = value < 0 ? '-' : '';
  const body = fraction ? `${grouped}${shape.decimalSep}${fraction}` : grouped;
  return `${shape.prefix}${sign}${body}${shape.suffix}`;
};

const tween = (el: HTMLElement, from: number, to: number, shape: NumberShape, finalText: string) => {
  const start = performance.now();
  // Même courbe que `--motion-ease-out` : le chiffre part vite et se pose.
  const ease = (t: number) => 1 - Math.pow(1 - t, 3);

  const settle = () => {
    // On repose toujours le texte du serveur, jamais le nôtre reconstruit : lui seul fait foi.
    el.textContent = finalText;
    el.removeAttribute('aria-busy');
  };

  // Filet : `requestAnimationFrame` ne se déclenche pas dans un onglet caché. Si l'utilisateur
  // part sur un autre onglet pendant l'écriture, la boucle s'arrête où elle en est — le chiffre
  // resterait sur une valeur intermédiaire et `aria-busy` ne serait jamais levé. Ce minuteur,
  // lui, court même en arrière-plan.
  const safety = window.setTimeout(settle, TWEEN_MS * 3);

  const step = (now: number) => {
    // Le `now` d'un rAF est l'heure de début de l'image : il peut précéder le `performance.now()`
    // pris au départ. Sans le plancher, `t` passe sous 0 et l'ease-out extrapole au-delà de la
    // valeur de départ (-1236,57 affiché pour un solde parti de -1234,56).
    const t = Math.min(1, Math.max(0, (now - start) / TWEEN_MS));
    if (t >= 1) {
      window.clearTimeout(safety);
      settle();
      return;
    }
    el.textContent = formatNumber(from + (to - from) * ease(t), shape);
    requestAnimationFrame(step);
  };

  // Le lecteur d'écran doit entendre le résultat, pas le défilement.
  el.setAttribute('aria-busy', 'true');
  requestAnimationFrame(step);
};

const applyDiff = (region: Element, before: Snapshot) => {
  const after = takeSnapshot(region);

  region.querySelectorAll<HTMLElement>('[data-motion-amount]').forEach((el) => {
    const key = el.dataset.motionAmount || '';
    const previous = before.amounts.get(key);
    const finalText = el.textContent?.trim() || '';
    if (previous === undefined || previous === finalText) return;

    const from = readNumber(previous);
    const to = readNumber(finalText);
    if (!from || !to) return;

    // Onglet caché : personne ne regarde le chiffre changer, et `requestAnimationFrame` n'y
    // tourne pas. Le texte du serveur est déjà en place, il n'y a rien à faire.
    if (document.hidden || prefersReducedMotion()) return;

    tween(el, from.value, to.value, to.shape, finalText);
  });

  // Une jauge (barre de progression du parcours) arrive déjà à sa nouvelle largeur : on lui
  // réinjecte l'ancienne, on force un calcul de style, puis on repose la nouvelle. La
  // transition CSS a alors un point de départ et la barre AVANCE au lieu de sauter.
  //
  // Pas de garde `prefers-reduced-motion` ici : c'est une transition CSS, donc le garde-fou
  // global de `config/_motion.scss` la ramène déjà à 0,01 ms.
  region.querySelectorAll<HTMLElement>('[data-motion-width]').forEach((el) => {
    const key = el.dataset.motionWidth || '';
    const previous = before.widths.get(key);
    const target = el.style.getPropertyValue('--workflow-progress').trim();
    if (!previous || !target || previous === target) return;

    // La transition est COUPÉE le temps de la réinjection, sinon elle s'applique aussi au retour
    // en arrière et annule tout : poser l'ancienne largeur lance une transition vers elle, si
    // bien qu'au pas d'après la largeur courante est encore la nouvelle — on repose donc la même
    // valeur, et la barre saute. C'est exactement ce qu'on voyait ; mesuré dans le navigateur, la
    // largeur ne bougeait pas d'un pixel entre les deux écritures.
    el.style.transition = 'none';
    el.style.setProperty('--workflow-progress', previous);
    // Lecture volontaire : sans elle le navigateur regroupe les écritures et rien ne bouge.
    void el.offsetWidth;
    // Ligne vidée, pas remise à sa valeur : la déclaration de la feuille de style reprend la main
    // (durée et courbe restent définies à un seul endroit, cf. `_workflow_step_shell.scss`).
    el.style.transition = '';
    el.style.setProperty('--workflow-progress', target);
  });

  // Élément VRAIMENT nouveau — une étape que la nav vient de révéler, par exemple. La nav est
  // re-rendue en entier à chaque passage : tous ses nœuds sont neufs, et une animation CSS posée
  // à plat les ferait tous clignoter ensemble. C'est la comparaison des clés, et elle seule, qui
  // distingue « cette ligne vient d'apparaître » de « cette ligne a été redessinée à l'identique ».
  //
  // Rien au tout premier rendu : la région n'avait alors aucune clé, donc tout serait « nouveau ».
  if (before.present.size > 0) {
    region.querySelectorAll<HTMLElement>('[data-motion-enter]').forEach((el) => {
      const key = el.dataset.motionEnter || '';
      if (before.present.has(key)) return;

      el.classList.add('motion-entered');
    });
  }

  const changed = [...after.rows.entries()].filter(([key, text]) => {
    const previous = before.rows.get(key);
    // Une ligne absente avant est une ligne NOUVELLE : elle s'allume aussi, c'est souvent
    // l'écriture qu'on vient de créer.
    return previous === undefined || previous !== text;
  });

  if (changed.length === 0 || changed.length > CHANGED_ROWS_CEILING) return;

  changed.forEach(([key]) => {
    region
      .querySelector<HTMLElement>(`[data-motion-key="${CSS.escape(key)}"]`)
      ?.classList.add('motion-row-changed');
  });
};

export function initializeValueChangeMotion() {
  if ((window as any).__valueChangeMotionInitialized) return;
  (window as any).__valueChangeMotionInitialized = true;

  // Avant le remplacement : on photographie l'état courant. `turbo:before-frame-render` est
  // émis sur la frame, l'ancien contenu est encore en place.
  document.addEventListener('turbo:before-frame-render', (event) => {
    const region = (event.target as Element | null)?.closest?.(REGION_SELECTOR);
    if (!region) return;

    const snapshot = takeSnapshot(region);
    snapshots.set(region, snapshot);
    const key = regionKey(region);
    if (key) snapshotsById.set(key, snapshot);

    // Et on emballe le rendu, comme pour le turbo stream : sur `turbo:frame-load` seul, le
    // navigateur a déjà eu le temps de peindre la nouvelle nav. La barre s'affichait donc une
    // image à sa largeur d'arrivée, avant d'être renvoyée à l'ancienne et de refaire le chemin
    // — un aller-retour bien visible sur le bouton d'édition, qui recule de plusieurs étapes.
    // En emballant, la réinjection a lieu dans la même tâche que le remplacement : rien n'est
    // peint entre les deux.
    //
    // `turbo:frame-load` reste branché en repli pour les rendus où l'on n'a pas pu emballer.
    const detail = (event as CustomEvent).detail;
    const originalRender = detail?.render;
    if (typeof originalRender !== 'function') return;

    detail.render = async (currentElement: HTMLElement, newElement: HTMLElement) => {
      await originalRender(currentElement, newElement);
      if (!consumeSnapshot(region, snapshot)) return;

      applyDiff(region, snapshot);
    };
  });

  document.addEventListener('turbo:frame-load', (event) => {
    const target = event.target as Element | null;
    const region = target?.closest?.(REGION_SELECTOR) || target?.querySelector?.(REGION_SELECTOR);
    if (!region) return;

    const before = consumeSnapshot(region);
    if (!before) return;

    applyDiff(region, before);
  });

  // Second chemin : le turbo stream. Avancer d'une étape ne rend pas une frame, ça remplace la
  // nav (`turbo_stream.replace steps_recap_*`). L'élément visé disparaît donc avec l'ancienne
  // nav — c'est la région marquée AUTOUR de lui, qui survit, qu'on compare.
  //
  // On emballe `event.detail.render` plutôt que d'écouter deux événements : c'est le seul
  // endroit où l'on tient à la fois l'avant (il n'a pas encore été appelé) et l'après.
  document.addEventListener('turbo:before-stream-render', (event) => {
    const detail = (event as CustomEvent).detail;
    const stream = detail?.newStream as HTMLElement | undefined;
    const targetId = stream?.getAttribute('target');
    if (!targetId) return;

    const region = document.getElementById(targetId)?.closest(REGION_SELECTOR);
    if (!region) return;

    const originalRender = detail.render;
    detail.render = async (streamElement: HTMLElement) => {
      const before = takeSnapshot(region);
      await originalRender(streamElement);
      applyDiff(region, before);
    };
  });
}
